// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import { X as CloseIcon, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useModalDialog } from '../../hooks/useModalDialog'
import styles from './WhatsNew.module.css'
import type { ReleaseInfo } from './useWhatsNew'

interface WhatsNewProps {
  versions: ReleaseInfo[]
  onClose: () => void
  onDismiss: () => void
}

function isVideoUrl(url: string): boolean {
  const clean = url.split('?')[0]?.split('#')[0]?.toLowerCase() ?? ''
  return clean.endsWith('.mp4') || clean.endsWith('.webm') || clean.endsWith('.mov')
}

function resolveAssetPath(src: string | undefined): string {
  if (!src) return ''
  if (src.startsWith('./')) return `/whatsnew/${src.slice(2)}`
  if (src.startsWith('../')) return `/whatsnew/${src}`
  if (!src.startsWith('/') && !src.startsWith('http')) return `/whatsnew/${src}`
  return src
}

function buildMarkdownComponents(t: (key: string) => string) {
  return {
    img: ({ src, alt }: { src?: string; alt?: string; node?: unknown }) => {
      const resolved = resolveAssetPath(src)
      if (resolved && isVideoUrl(resolved)) {
        return (
          <video src={resolved} controls className={styles.video} aria-label={alt || ''}>
            {t('videoNotSupported')}
          </video>
        )
      }
      return <img src={resolved} alt={alt || ''} className={styles.image} />
    },
    a: ({ href, children }: { href?: string; children?: React.ReactNode; node?: unknown }) => (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  }
}

function ReleaseContent({ version, contentCache, isLoading }: {
  version: string
  contentCache: Record<string, string>
  isLoading: boolean
}): ReactElement {
  const { t } = useTranslation('common')
  const content = contentCache[version]
  const mdComponents = useMemo(() => buildMarkdownComponents(t), [t])

  if (isLoading && !content) {
    return <div className={styles.loading}>{t('loading')}</div>
  }

  if (!content) {
    return <div className={styles.loading}>{t('noContentAvailable')}</div>
  }

  return (
    <div className={styles.markdownContent}>
      <Markdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        components={mdComponents}
      >
        {content}
      </Markdown>
    </div>
  )
}

export function WhatsNew({ versions, onClose, onDismiss }: WhatsNewProps): ReactElement {
  const { t, i18n } = useTranslation('common')
  const [activeTab, setActiveTab] = useState<string>(versions[0]?.version ?? '')
  const [moreDetailVersion, setMoreDetailVersion] = useState<string | null>(null)
  const [contentCache, setContentCache] = useState<Record<string, string>>({})
  const [loadingVersion, setLoadingVersion] = useState<string | null>(null)

  const lang = i18n.language?.split('-')[0] ?? 'en'
  const tabVersions = versions.slice(0, 3)

  const fetchContent = useCallback(async (version: string) => {
    if (contentCache[version]) return
    setLoadingVersion(version)
    try {
      let text: string | null = null
      if (lang !== 'en') {
        const localeRes = await fetch(`/whatsnew/${lang}/v${version}.md`)
        if (localeRes.ok) text = await localeRes.text()
      }
      if (!text) {
        const res = await fetch(`/whatsnew/v${version}.md`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        text = await res.text()
      }
      setContentCache(prev => ({ ...prev, [version]: text }))
    } catch {
      setContentCache(prev => ({
        ...prev,
        [version]: `*${t('releaseNotesUnavailable', { version })}*`,
      }))
    } finally {
      setLoadingVersion(null)
    }
  }, [contentCache, t, lang])

  // Load content when active tab or more-detail changes
  useEffect(() => {
    const target = activeTab === 'more' ? moreDetailVersion : activeTab
    if (target) void fetchContent(target)
  }, [activeTab, moreDetailVersion, fetchContent])

  // Focus trap + Escape-to-close + focus return. This modal mounts only
  // when open, so it is always "open" while rendered.
  const { dialogRef } = useModalDialog<HTMLDivElement>(true, onClose)

  const currentVersion = activeTab === 'more' ? moreDetailVersion : activeTab

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        ref={dialogRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="whatsnew-title"
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="whatsnew-title" className={styles.title}>{t('whatsNew')}</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label={t('close')}>
            <CloseIcon size={18} />
          </button>
        </div>

        {/* Tab Strip */}
        <div className={styles.tabStrip}>
          {tabVersions.map(v => (
            <button
              key={v.version}
              className={`${styles.tab} ${activeTab === v.version ? styles.tabActive : ''}`}
              onClick={() => {
                setActiveTab(v.version)
                setMoreDetailVersion(null)
              }}
            >
              v{v.version}
            </button>
          ))}
          <button
            className={`${styles.tab} ${activeTab === 'more' ? styles.tabActive : ''}`}
            onClick={() => {
              setActiveTab('more')
              setMoreDetailVersion(null)
            }}
          >
            {t('more')}
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {activeTab === 'more' && !moreDetailVersion ? (
            /* Version history list */
            <div className={styles.versionList}>
              <h3 className={styles.historyTitle}>{t('versionHistory')}</h3>
              {versions.map(v => (
                <button
                  key={v.version}
                  className={styles.versionItem}
                  onClick={() => setMoreDetailVersion(v.version)}
                >
                  <div className={styles.versionItemLeft}>
                    <span className={styles.versionItemVersion}>v{v.version}</span>
                    <span className={styles.versionItemTitle}>{v.title}</span>
                    {v.summary && (
                      <span className={styles.versionItemSummary}>{v.summary}</span>
                    )}
                  </div>
                  <div className={styles.versionItemRight}>
                    <span className={styles.versionItemDate}>{v.date}</span>
                    <ChevronRight size={16} />
                  </div>
                </button>
              ))}
            </div>
          ) : activeTab === 'more' && moreDetailVersion ? (
            /* Detail view from More tab */
            <div>
              <button className={styles.backButton} onClick={() => setMoreDetailVersion(null)}>
                <ChevronLeft size={16} />
                {t('versionHistory')}
              </button>
              <ReleaseContent
                version={moreDetailVersion}
                contentCache={contentCache}
                isLoading={loadingVersion === moreDetailVersion}
              />
            </div>
          ) : currentVersion ? (
            /* Regular version tab content */
            <ReleaseContent
              version={currentVersion}
              contentCache={contentCache}
              isLoading={loadingVersion === currentVersion}
            />
          ) : null}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.dismissButton} onClick={onDismiss}>
            {t('doNotShowAgain')}
          </button>
        </div>
      </div>
    </div>
  )
}
